/**
 * Which cards changed column since you last looked.
 *
 * Dragging a card across the board and letting go is the one moment where the
 * thing you were holding stops being where your eye is. Seven columns, a
 * filter possibly hiding half of them, and the card reappears somewhere in a
 * stack — so the board briefly rings whatever just arrived.
 *
 * Diffing the list rather than remembering the drop means it also rings work
 * moved from the detail drawer, and work somebody else moved that arrived on
 * the next refresh. The drop is not the event; the arrival is.
 */

export interface Placed {
  id: string;
  status: string;
}

export interface Watched {
  /** Card id to the column it was last seen in. */
  seen: Map<string, string>;
  /**
   * False until the first list has been recorded.
   *
   * Without this, every card on the board rings on first paint, which is a
   * board that looks like it is having a seizure rather than one telling you
   * something.
   */
  primed: boolean;
}

export const NOTHING_WATCHED: Watched = { seen: new Map(), primed: false };

export interface Arrivals {
  /** Ids that moved, or appeared, since the last call. */
  moved: string[];
  after: Watched;
}

export function arrivalsSince(before: Watched, items: readonly Placed[]): Arrivals {
  const seen = new Map<string, string>();
  const moved: string[] = [];

  for (const item of items) {
    const was = before.seen.get(item.id);
    // A card that was not there a moment ago has arrived just as much as one
    // that moved — but only once we know what "a moment ago" looked like.
    if (before.primed && was !== item.status) moved.push(item.id);
    seen.set(item.id, item.status);
  }

  // Rebuilt rather than mutated, so cards that were deleted or filtered out
  // do not sit in the map and ring on their way back.
  return { moved, after: { seen, primed: true } };
}
